import {
  ADDRESS_FIELD_ORDER,
  LEASE_COMP_PINNED_FIELD_ORDER,
} from "@/constants/base";
import {
  DetailedSurvey,
  LeaseCompFieldRule,
  PropertyFieldRule,
} from "@/features/survey/types/survey.core.types";
import type { MediaFile } from "@survey-types/survey.media.types";
import { Property } from "@survey-types/survey.property.types";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { createElement, Fragment } from "react";

export const formatDate = (
  date: Date | string | number | null | undefined,
  format = "MMM. D, YYYY",
): string => {
  return dayjs(date).format(format);
};

export const isEmptyObjectFromStr = (inputStr: string) =>
  Object.keys(JSON.parse(inputStr)).length === 0;

/**
 * Compares current and existing values to determine if they have changed.
 * Handles type conversion, null/undefined values, and automatic whitespace trimming.
 */
export const checkValueChanged = (
  currentValue: unknown,
  existingValue: unknown,
): boolean => {
  const currentStr = String(currentValue ?? "").trim();
  const existingStr = String(existingValue ?? "").trim();
  return currentStr !== existingStr;
};

// Storage utility functions
export const StorageUtil = {
  cleanStorage: () => {
    sessionStorage.clear();
    localStorage.clear();
  },
  setSessionItems: (items: Record<string, string>) => {
    Object.entries(items).forEach(([key, value]) => {
      sessionStorage.setItem(key, value);
    });
  },
  getSessionItem: (key: string): string | null => {
    return sessionStorage.getItem(key);
  },
  removeSessionItem: (key: string): void => {
    sessionStorage.removeItem(key);
  },
};

export const checkValueIsUnavailable = (obj: unknown) =>
  obj === null || obj === undefined || obj === "" || Number.isNaN(obj);

export const getArrayChunks = <T>(originArray: T[], size: number): T[][] => {
  if (!Array.isArray(originArray) || size <= 0) {
    return [];
  }
  const newArray = Array.from(
    new Array(Math.ceil(originArray.length / size)),
    (_, i) => originArray.slice(i * size, i * size + size),
  );
  return newArray;
};

export const surveyShortlistedOverride = (property: Property) =>
  property.isClientShortlist && !property.isHidden;

/**
 * Get field labels for all active (non-hidden) rules.
 * This function works for both regular fields and custom fields.
 */
export const getActiveFieldLabelList = (
  propertyFieldRuleList: PropertyFieldRule[],
) => {
  return propertyFieldRuleList
    ?.filter((rule) => !rule.isHidden)
    .map((rule) => rule.fieldLabel)
    .filter((label): label is string => label != null);
};

export const mediaFileToGalleryFile = (media: MediaFile) => ({
  id: media.mediaId,
  url:
    media.origin !== "SiteIQ" ? `${media.cloudfrontUrl}` : media.cloudfrontUrl,
  previewUrl: media.pdfImageUrl,
  thumbnailUrl: media.thumbnailUrl,
  displayText: media.displayName,
  active: !media.isHidden,
  primary: false,
  order: media.order,
  deletable: true,
  origin: media.origin,
  modifiedFromMediaId: media.modifiedFromMediaId,
  mediaId: media.mediaId,
});

/**
 * Scrolls to a specific section within the builder-main container
 * @param sectionId - The ID of the section to scroll to
 */
export const scrollToSection = (sectionId: string) => {
  // Use a more specific query to ensure we're targeting the right elements
  const builderMain = document.querySelector(".builder-main") as HTMLElement;
  // using id for section causes issues with duplicates and timing so data attribute is better for react
  const targetSection = document.querySelector(
    `[data-section-id="${sectionId}"]`,
  ) as HTMLElement;

  if (builderMain && targetSection) {
    // Check if URL contains innerapp or localStorage has showAppShell false
    const isInnerApp = window.location.pathname.includes("innerapp");
    const isHeaderToggleOff = localStorage.getItem("showAppShell") === "false";

    // Base offset for headers, decrease by 48 if innerapp or header toggle is off
    const headerOffset = isInnerApp || isHeaderToggleOff ? 72 : 120; // 120 - 48 = 72

    builderMain.scrollTo({
      top: targetSection.offsetTop - headerOffset,
    });
  }
};

/**
 * Check if a value is a valid URL
 * Uses regex pattern to detect URLs including those with query parameters
 * Supports URLs with or without http/https protocol (e.g., www.example.com)
 * @param value - The value to check
 * @returns true if the value is a valid URL string, false otherwise
 */
export const isUrl = (value: unknown): boolean => {
  if (typeof value !== "string") return false;

  const trimmed = value.trim();

  // Regex pattern to match URLs with http/https protocol
  const urlWithProtocol = /^https?:\/\/[^\s<>"']+$/i;
  if (urlWithProtocol.test(trimmed)) return true;

  // Match URLs without protocol (www. prefix or domain.tld pattern)
  const urlWithoutProtocol =
    /^(www\.[^\s<>"']+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"']*)$/i;
  return urlWithoutProtocol.test(trimmed);
};

/**
 * Helper to split URL with wbr tags
 */
const splitUrlWithWbr = (url: string, charsPerLine: number): ReactNode[] => {
  return Array.from(
    { length: Math.ceil(url.length / charsPerLine) },
    (_, i) => {
      const start = i * charsPerLine;
      const part = url.slice(start, start + charsPerLine);
      const isLastPart = start + charsPerLine >= url.length;
      return isLastPart ? part : [part, createElement("wbr")];
    },
  ).flat();
};

/**
 * Check if string contains HTML tags
 */
const containsHtmlTags = (str: string): boolean => {
  return /<[^>]+>/i.test(str);
};

/**
 * Process text content and convert URLs to clickable links with wbr tags for long URLs
 * Also handles splitting long text without spaces
 * Supports URLs with or without http/https protocol
 */
const processTextWithUrls = (
  text: string,
  charsPerLine?: number,
): ReactNode[] => {
  // Find all URLs in the text (with or without protocol)
  const urlRegex =
    /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|(?:^|\s)([a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"']*))/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0].trim();
    const matchIndex = text.indexOf(url, lastIndex);
    // Process text before the URL (might need wbr splitting if no spaces or long continuous segments)
    if (matchIndex > lastIndex) {
      const beforeText = text.slice(lastIndex, matchIndex);
      const hasNoSpaces = !beforeText.includes(" ");
      const textWithoutSpaces = beforeText.replace(/\s/g, "");

      if (
        charsPerLine &&
        (hasNoSpaces || textWithoutSpaces.length > 35) &&
        beforeText.length > charsPerLine
      ) {
        // Split long text without spaces or with long continuous segments
        parts.push(...splitUrlWithWbr(beforeText, charsPerLine));
      } else {
        parts.push(beforeText);
      }
    }

    // Create a link element for the URL
    // Add https:// protocol if URL doesn't have one
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;

    const shouldSplitUrl = charsPerLine && url.length > charsPerLine;
    const linkContent = shouldSplitUrl
      ? splitUrlWithWbr(url, charsPerLine)
      : url;

    const linkElement = createElement(
      "a",
      {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "text-primary hover:underline",
      },
      ...(Array.isArray(linkContent) ? linkContent : [linkContent]),
    );

    parts.push(linkElement);
    lastIndex = matchIndex + url.length;
  }

  // Process remaining text after the last URL
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    /* const hasNoSpaces = !remainingText.includes(" ");
    const textWithoutSpaces = remainingText.replace(/\s/g, ""); */
    parts.push(remainingText);
    /* if (
      charsPerLine &&
      (hasNoSpaces || textWithoutSpaces.length > 35) &&
      remainingText.length > charsPerLine
    ) {
      // Split long text without spaces or with long continuous segments
      parts.push(...splitUrlWithWbr(remainingText, charsPerLine));
    } else {
      parts.push(remainingText);
    } */
  }

  return parts.length > 0 ? parts : [text];
};

/**
 * Render a value as a hyperlink if it's a URL, otherwise return the value as-is
 * URLs are rendered with text-primary styling and open in a new tab
 * For text containing HTML tags, treats them as plain text but splits long URLs with wbr tags
 * @param value - The value to render
 * @param fieldKey - Optional field key for conditional rendering
 * @param charsPerLine - Optional character limit for splitting URLs with wbr tags
 * @returns A React element (hyperlink or text) or the original value
 */
export const renderUrlOrValue = (
  value: unknown,
  fieldKey?: string | undefined | null,
  charsPerLine?: number,
): ReactNode | string => {
  if (typeof value !== "string") {
    return String(value);
  }
  // Check if it's a plain URL (not HTML)
  if (isUrl(value)) {
    // Add https:// protocol if URL doesn't have one
    const href = /^https?:\/\//i.test(value) ? value : `https://${value}`;

    const linkProps = {
      href,
      target: "_blank" as const,
      rel: "noopener noreferrer",
      className: "text-primary hover:underline",
    };

    const shouldSplitUrl = charsPerLine && value.length > charsPerLine;
    const linkContent = shouldSplitUrl
      ? splitUrlWithWbr(value, charsPerLine)
      : value;

    return createElement(
      "a",
      linkProps,
      ...(Array.isArray(linkContent) ? linkContent : [linkContent]),
    );
  }

  // Check if content contains HTML tags - treat as plain text but process URLs
  if (containsHtmlTags(value)) {
    const processedContent = processTextWithUrls(value, charsPerLine);
    return createElement(Fragment, {}, ...processedContent);
  }

  // Regular text content with line breaks
  const hasLineBreaks = value.includes("\n");
  // Wrap in div if it has line breaks OR if it's the address1 field
  const shouldWrapInDiv = hasLineBreaks || fieldKey === "address1";

  return value.split("\n").map((line, index) => {
    const isEmptyLine = line.trim() === "";
    // Use index combined with content hash for stable, unique keys
    const contentHash =
      line.length > 0 ? line.slice(0, 5).replace(/\s/g, "") : "empty";
    const uniqueKey = `line-${index}-${contentHash}`;

    // Process line for URLs (handles text with embedded URLs like "Visit https://example.com")
    const processedContent = processTextWithUrls(line, charsPerLine);
    if (shouldWrapInDiv) {
      return createElement(
        "div",
        { key: uniqueKey, className: isEmptyLine ? "" : undefined },
        ...processedContent,
      );
    } else {
      // If processedContent contains React elements (e.g. <a> tags for URLs),
      // joining as strings would produce "[object Object]". Use a Fragment instead.
      const hasReactElements = processedContent.some(
        (part) => typeof part !== "string",
      );
      if (hasReactElements || processedContent.length > 1) {
        return createElement(Fragment, { key: uniqueKey }, ...processedContent);
      }
      return processedContent.join("");
    }
  });
};

export const hasAddressLine2Field = (currentSurvey: DetailedSurvey) => {
  return currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.some(
    (rule) => rule.fieldLabel === "Address Line 2" && rule.fieldKey,
  );
};

export const hasAddressLine2AsCustom = (currentSurvey: DetailedSurvey) => {
  return (
    currentSurvey?.customFields?.some(
      (field) => field.originalName === "Address Line 2",
    ) ||
    currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.some(
      (rule) => rule.fieldLabel === "Address Line 2" && !rule.fieldKey,
    )
  );
};

/**
 * Determines the correct order for an address field in a target list
 * by checking how many address fields appear before it in both active and inactive lists.
 * @param fieldKey - The field key to find the order for
 * @param targetList - The list where the field will be inserted (active or inactive)
 * @param activeList - The current active property field list
 * @param inactiveList - The current inactive property field list
 * @returns The order index for the field in the target list
 */
export const getAddressFieldOrder = (
  fieldKey: string,
  targetList: PropertyFieldRule[],
  activeList: PropertyFieldRule[],
  inactiveList: PropertyFieldRule[],
  hasAddressLine2AsCustom?: boolean,
): number => {
  const adjustedAddressFieldOrder = hasAddressLine2AsCustom
    ? ADDRESS_FIELD_ORDER.filter((key) => key !== "address2")
    : ADDRESS_FIELD_ORDER;
  const fieldIndex = adjustedAddressFieldOrder.indexOf(fieldKey);
  if (fieldIndex === -1) return targetList.length;

  // Count how many address fields appear before this field in both lists
  const addressFieldsInActive = adjustedAddressFieldOrder
    .slice(0, fieldIndex)
    .filter((key) => activeList.some((f) => f.fieldKey === key)).length;

  const addressFieldsInInactive = adjustedAddressFieldOrder
    .slice(0, fieldIndex)
    .filter((key) => inactiveList.some((f) => f.fieldKey === key)).length;

  // Return the count in the target list
  return targetList === activeList
    ? addressFieldsInActive
    : addressFieldsInInactive;
};

/**
 * Determines the correct order for pinned lease comp fields in a target list
 * by checking how many pinned fields appear before it in active and inactive lists.
 * @param fieldKeyName - The lease comp keyName to find the order for
 * @param targetList - The list where the field will be inserted (active or inactive)
 * @param activeList - The current active lease comp field list
 * @param inactiveList - The current inactive lease comp field list
 * @returns The order index for the field in the target list
 */
export const getLeaseCompFieldOrder = (
  fieldKeyName: string,
  targetList: LeaseCompFieldRule[],
  activeList: LeaseCompFieldRule[],
  inactiveList: LeaseCompFieldRule[],
): number => {
  const fieldIndex = LEASE_COMP_PINNED_FIELD_ORDER.indexOf(fieldKeyName);
  if (fieldIndex === -1) return targetList.length;

  const pinnedFieldsInActive = LEASE_COMP_PINNED_FIELD_ORDER.slice(
    0,
    fieldIndex,
  ).filter((key) => activeList.some((f) => f.keyName === key)).length;

  const pinnedFieldsInInactive = LEASE_COMP_PINNED_FIELD_ORDER.slice(
    0,
    fieldIndex,
  ).filter((key) => inactiveList.some((f) => f.keyName === key)).length;

  return targetList === activeList
    ? pinnedFieldsInActive
    : pinnedFieldsInInactive;
};
